import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MapPin, Mail, Phone, Building2, Home, Briefcase, Send, Loader2,
  ChevronDown, Check, ArrowRight,
} from 'lucide-react';
import { API } from '../utils/apiBase';

// ============================================================
// Public listing page for Adam's Way (or any public property).
// Route: /property/:slug — no auth required.
// ============================================================

const STATUS_LABEL = {
  for_sale: 'For Sale',
  rental: 'For Rent',
  subsidized: 'Subsidized',
  rental_or_sale: 'Rent or Buy',
};
const STATUS_TONE = {
  for_sale: { bg: '#1B5E20', fg: '#E8F5E9' },
  rental: { bg: '#0D47A1', fg: '#E3F2FD' },
  subsidized: { bg: '#4A148C', fg: '#F3E5F5' },
  rental_or_sale: { bg: '#E65100', fg: '#FFF3E0' },
};
const TYPE_LABEL = {
  studio: 'Studio', '1_bed': '1 Bed', '2_bed': '2 Bed', '3_bed': '3 Bed',
  commercial: 'Commercial', office: 'Office', retail: 'Retail',
};
const FLOOR_LABEL = {
  G: 'Ground / Commercial',
  A: '1st Floor',
  B: '2nd Floor',
  C: '3rd Floor',
};

const fmtUSD = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export default function PropertyListingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedUnit, setSelectedUnit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/public/property/${slug}`);
      setData(resp.data);
    } catch (e) {
      setData(null);
    }
    setLoading(false);
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F7F4ED' }}>
        <Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#F7F4ED' }}>
        <Home className="h-10 w-10 text-[#8E85A0] mb-3" />
        <h1 className="text-2xl font-bold text-[#2D1B4E] mb-2">Listing not found</h1>
        <p className="text-sm text-[#5A4D6E] mb-6">This property is either offline or doesn't exist.</p>
        <Link to="/login" className="text-sm text-[#6B3FA0] underline">Go to TNSP login</Link>
      </div>
    );
  }

  // Stats
  const forSale = data.units.filter(u => u.status === 'for_sale' || u.status === 'rental_or_sale');
  const forRent = data.units.filter(u => u.status === 'rental' || u.status === 'subsidized' || u.status === 'rental_or_sale');
  const commercial = data.units.filter(u => ['commercial', 'office', 'retail'].includes(u.type));

  let filteredUnits = data.units;
  if (filter === 'sale') filteredUnits = forSale.filter(u => !['commercial', 'office', 'retail'].includes(u.type));
  if (filter === 'rent') filteredUnits = forRent.filter(u => !['commercial', 'office', 'retail'].includes(u.type));
  if (filter === 'commercial') filteredUnits = commercial;

  // Cheapest of each type for hero stats
  const minSale = forSale.filter(u => u.price > 0).map(u => u.price).sort((a, b) => a - b)[0];
  const minRent = forRent.filter(u => u.monthly_rent > 0 && !['commercial', 'office', 'retail'].includes(u.type)).map(u => u.monthly_rent).sort((a, b) => a - b)[0];

  return (
    <div className="min-h-screen" style={{ background: '#F7F4ED', fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="public-listing">
      <style>{`
        .marketing-serif { font-family: 'Crimson Pro', Georgia, serif; }
        .marketing-tracking { letter-spacing: 0.15em; }
      `}</style>

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background: data.rendering_b64
            ? `linear-gradient(180deg, rgba(20,12,40,0.55) 0%, rgba(20,12,40,0.85) 100%), url(${data.rendering_b64}) center / cover no-repeat`
            : 'linear-gradient(135deg, #2D1B4E 0%, #6B3FA0 100%)',
          color: 'white',
        }}
        data-testid="hero">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          {data.location && (
            <div className="flex items-center gap-2 text-xs uppercase marketing-tracking opacity-80 mb-3">
              <MapPin className="h-3.5 w-3.5" /> {data.location}
            </div>
          )}
          <h1 className="marketing-serif text-5xl md:text-7xl font-semibold leading-[1.05] mb-4" data-testid="property-name">
            {data.name}
          </h1>
          {data.tagline && (
            <p className="marketing-serif italic text-xl md:text-2xl opacity-90 mb-2 max-w-2xl">
              {data.tagline}
            </p>
          )}
          {data.headline && (
            <p className="text-base md:text-lg opacity-80 max-w-2xl mb-8">
              {data.headline}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-8">
            <a href="#units" className="bg-white text-[#2D1B4E] hover:bg-[#F0E9D6] px-6 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2 transition" data-testid="cta-view-units">
              View {data.unit_count} available units <ArrowRight className="h-4 w-4" />
            </a>
            <a href="#inquire" className="border border-white/40 hover:bg-white/10 px-6 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2 transition" data-testid="cta-inquire">
              Request more info
            </a>
          </div>

          {/* Hero quick-stats */}
          <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/20">
            <HeroStat label="Available units" value={data.unit_count} />
            {minSale && <HeroStat label="From (sale)" value={`${fmtUSD(minSale)}+`} />}
            {minRent && <HeroStat label="From (rent)" value={`${fmtUSD(minRent)}/mo+`} />}
            {commercial.length > 0 && <HeroStat label="Commercial spaces" value={commercial.length} />}
          </div>
        </div>
      </section>

      {/* About */}
      {data.listing_blurb && (
        <section className="max-w-4xl mx-auto px-6 md:px-10 py-16">
          <div className="text-xs uppercase marketing-tracking text-[#8B5A2B] mb-3 font-semibold">About the property</div>
          <p className="marketing-serif text-xl md:text-2xl leading-relaxed text-[#2D1B4E]">
            {data.listing_blurb}
          </p>
        </section>
      )}

      {/* Units */}
      <section id="units" className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-xs uppercase marketing-tracking text-[#8B5A2B] mb-2 font-semibold">Available units</div>
            <h2 className="marketing-serif text-3xl md:text-4xl font-semibold text-[#2D1B4E]">
              Choose your space
            </h2>
          </div>
          <div className="flex items-center gap-1 p-1 bg-white border border-[#E0D8E8] rounded-lg" data-testid="unit-filters">
            {[
              { k: 'all', l: `All · ${data.units.length}` },
              { k: 'sale', l: `For Sale · ${forSale.filter(u => !['commercial', 'office', 'retail'].includes(u.type)).length}` },
              { k: 'rent', l: `For Rent · ${forRent.filter(u => !['commercial', 'office', 'retail'].includes(u.type)).length}` },
              ...(commercial.length > 0 ? [{ k: 'commercial', l: `Commercial · ${commercial.length}` }] : []),
            ].map(f => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition ${filter === f.k ? 'bg-[#2D1B4E] text-white' : 'text-[#5A4D6E] hover:bg-[#F4EFFA]'}`}
                data-testid={`filter-${f.k}`}>
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {filteredUnits.length === 0 ? (
          <div className="bg-white border border-[#E0D8E8] rounded-md p-12 text-center text-[#8E85A0]">
            No units match this filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredUnits.map(u => (
              <UnitCard
                key={u.id}
                unit={u}
                onInquire={() => {
                  setSelectedUnit(u);
                  document.getElementById('inquire')?.scrollIntoView({ behavior: 'smooth' });
                }}
              />
            ))}
          </div>
        )}
      </section>

      {/* Inquiry form */}
      <section
        id="inquire"
        className="py-20 px-6 md:px-10"
        style={{ background: 'linear-gradient(135deg, #2D1B4E 0%, #1A0F2E 100%)', color: 'white' }}
        data-testid="inquire-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-xs uppercase marketing-tracking opacity-70 mb-2 font-semibold">Talk to us</div>
          <h2 className="marketing-serif text-3xl md:text-5xl font-semibold mb-3">
            Request more information
          </h2>
          <p className="opacity-80 mb-8 max-w-xl">
            Tell us a little about yourself and we'll be in touch within one business day.
          </p>
          <InquiryForm slug={slug} selectedUnit={selectedUnit} onClearUnit={() => setSelectedUnit(null)} />

          {(data.contact_email || data.contact_phone) && (
            <div className="mt-10 pt-8 border-t border-white/15 flex flex-wrap gap-6 text-sm">
              {data.contact_email && (
                <a href={`mailto:${data.contact_email}`} className="inline-flex items-center gap-2 opacity-80 hover:opacity-100 transition">
                  <Mail className="h-4 w-4" /> {data.contact_email}
                </a>
              )}
              {data.contact_phone && (
                <a href={`tel:${data.contact_phone.replace(/[^0-9+]/g, '')}`} className="inline-flex items-center gap-2 opacity-80 hover:opacity-100 transition">
                  <Phone className="h-4 w-4" /> {data.contact_phone}
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center text-xs text-[#8E85A0]" style={{ background: '#F0EAE0' }}>
        © {new Date().getFullYear()} {data.name} · The Next Step Programs
      </footer>
    </div>
  );
}

// ---------- subcomponents ----------

function HeroStat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase marketing-tracking opacity-70 mb-1">{label}</div>
      <div className="marketing-serif text-3xl md:text-4xl font-semibold">{value}</div>
    </div>
  );
}

function UnitCard({ unit, onInquire }) {
  const isCommercial = ['commercial', 'office', 'retail'].includes(unit.type);
  const tone = STATUS_TONE[unit.status] || { bg: '#37474F', fg: 'white' };
  const showSale = unit.price > 0 && (unit.status === 'for_sale' || unit.status === 'rental_or_sale');
  const showRent = unit.monthly_rent > 0 && (unit.status === 'rental' || unit.status === 'subsidized' || unit.status === 'rental_or_sale' || isCommercial);
  return (
    <div
      className="bg-white border border-[#E0D8E8] rounded-md p-5 hover:shadow-lg hover:border-[#6B3FA0] transition cursor-pointer flex flex-col"
      onClick={onInquire}
      data-testid={`unit-card-${unit.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs uppercase marketing-tracking text-[#8E85A0]">{FLOOR_LABEL[unit.floor] || unit.floor}</div>
          <div className="marketing-serif text-2xl font-semibold text-[#2D1B4E] mt-0.5">
            {isCommercial ? <Briefcase className="h-4 w-4 inline mr-1 -mt-1 text-[#8B5A2B]" /> : <Home className="h-4 w-4 inline mr-1 -mt-1 text-[#6B3FA0]" />}
            {unit.unit_code}
          </div>
          <div className="text-sm text-[#5A4D6E] mt-1">{TYPE_LABEL[unit.type] || unit.type}</div>
        </div>
        <span
          className="text-[10px] uppercase font-bold px-2 py-1 rounded"
          style={{ background: tone.bg, color: tone.fg, letterSpacing: '0.08em' }}>
          {STATUS_LABEL[unit.status] || unit.status}
        </span>
      </div>

      <div className="space-y-1 mb-4 flex-1">
        {showSale && (
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-[#8E85A0]">Sale price</span>
            <span className="marketing-serif text-xl font-semibold text-[#2D1B4E]">{fmtUSD(unit.price)}</span>
          </div>
        )}
        {showRent && (
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-[#8E85A0]">Monthly rent</span>
            <span className="marketing-serif text-xl font-semibold text-[#2D1B4E]">{fmtUSD(unit.monthly_rent)}<span className="text-xs font-normal opacity-60">/mo</span></span>
          </div>
        )}
      </div>

      <button
        className="w-full bg-[#F4EFFA] hover:bg-[#6B3FA0] hover:text-white text-[#6B3FA0] text-xs font-semibold py-2 rounded transition inline-flex items-center justify-center gap-1"
        data-testid={`unit-inquire-${unit.id}`}>
        Inquire about {unit.unit_code} <ArrowRight className="h-3 w-3" />
      </button>
    </div>
  );
}

function InquiryForm({ slug, selectedUnit, onClearUnit }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', interest: 'any', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/property/${slug}/inquire`, {
        ...form,
        unit_id: selectedUnit?.id || '',
      });
      setDone(true);
      toast.success('Thanks — we\'ll be in touch soon.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="bg-white/10 border border-white/20 rounded-md p-8 text-center" data-testid="inquire-success">
        <div className="w-12 h-12 rounded-full bg-[#43A047] mx-auto flex items-center justify-center mb-3">
          <Check className="h-6 w-6 text-white" />
        </div>
        <h3 className="marketing-serif text-2xl font-semibold mb-2">Inquiry received</h3>
        <p className="opacity-80">Thanks {form.name.split(' ')[0]}. We'll reach out within one business day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="inquire-form">
      {/* Honeypot */}
      <input type="text" name="website" tabIndex="-1" autoComplete="off" value={form.website}
        onChange={e => setForm({ ...form, website: e.target.value })}
        style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} />

      {selectedUnit && (
        <div className="md:col-span-2 bg-[#FFD66B]/15 border border-[#FFD66B]/40 rounded p-3 flex items-center justify-between">
          <div className="text-sm">
            <span className="opacity-70">Asking about </span>
            <strong>{selectedUnit.unit_code}</strong>
            <span className="opacity-70"> · {TYPE_LABEL[selectedUnit.type] || selectedUnit.type}</span>
          </div>
          <button type="button" onClick={onClearUnit} className="text-xs underline opacity-70 hover:opacity-100">Clear</button>
        </div>
      )}

      <Field label="Your name *">
        <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
          className="inq-input" placeholder="Jane Doe" data-testid="inq-name" />
      </Field>
      <Field label="Email">
        <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
          className="inq-input" placeholder="jane@example.com" data-testid="inq-email" />
      </Field>
      <Field label="Phone">
        <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
          className="inq-input" placeholder="(555) 555-0100" data-testid="inq-phone" />
      </Field>
      <Field label="Interest">
        <div className="relative">
          <select value={form.interest} onChange={e => setForm({ ...form, interest: e.target.value })}
            className="inq-input appearance-none pr-8" data-testid="inq-interest">
            <option value="any">Open to either</option>
            <option value="sale">Buying</option>
            <option value="rental">Renting</option>
            <option value="commercial">Commercial space</option>
          </select>
          <ChevronDown className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 opacity-60 pointer-events-none" />
        </div>
      </Field>
      <div className="md:col-span-2">
        <Field label="Message">
          <textarea rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
            className="inq-input" placeholder="Tell us what you're looking for…" data-testid="inq-message" />
        </Field>
      </div>

      <div className="md:col-span-2">
        <button
          type="submit"
          disabled={submitting}
          className="bg-[#C8A45A] hover:bg-[#B0904A] text-[#2D1B4E] font-semibold px-6 py-3 rounded-md inline-flex items-center gap-2 transition disabled:opacity-50"
          data-testid="inq-submit">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submitting ? 'Sending…' : 'Send inquiry'}
        </button>
      </div>

      <style>{`
        .inq-input {
          width: 100%;
          padding: 10px 12px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          color: white;
          font-size: 14px;
          outline: none;
          transition: border-color 150ms ease, background 150ms ease;
        }
        .inq-input::placeholder { color: rgba(255, 255, 255, 0.45); }
        .inq-input:focus {
          border-color: #C8A45A;
          background: rgba(255, 255, 255, 0.1);
        }
        select.inq-input option { color: #2D1B4E; background: white; }
      `}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase marketing-tracking opacity-70 font-semibold mb-1.5">{label}</div>
      {children}
    </label>
  );
}
